import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { APP_VERSION } from '@/config/version';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar, 
  Car, 
  Receipt, 
  Trophy,
  User,
  ChevronRight,
  Clock,
  MapPin,
  AlertCircle,
  CheckCircle,
  Loader2,
  Users,
  Heart,
  Ticket,
  Download,
  ClipboardList,
  ShoppingBag,
  Tag,
  MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MemberDashboardPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [myVehicles, setMyVehicles] = useState([]);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [carOfMonth, setCarOfMonth] = useState(null);
  const [activeSurveys, setActiveSurveys] = useState([]);
  const [recentListings, setRecentListings] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // Fetch upcoming events
      try {
        const eventsRes = await axios.get(`${API_URL}/api/events?upcoming=true&limit=5`, { headers });
        setUpcomingEvents(eventsRes.data.events || []);
        
        // Find my registrations
        const myRegs = eventsRes.data.events?.filter(e => e.my_registration) || [];
        setMyRegistrations(myRegs);
      } catch (e) {}

      // Fetch my vehicles
      try {
        const vehiclesRes = await axios.get(`${API_URL}/api/vehicles`, { headers });
        setMyVehicles(vehiclesRes.data.vehicles || []);
      } catch (e) {}

      // Fetch my invoices
      try {
        const invoicesRes = await axios.get(`${API_URL}/api/profile/invoices`, { headers });
        const open = invoicesRes.data.filter(inv => inv.status === 'open');
        setOpenInvoices(open);
      } catch (e) {}

      // Fetch car of the month
      try {
        const period = new Date().toISOString().slice(0, 7);
        const cotmRes = await axios.get(`${API_URL}/api/voting/${period}/candidates`, { headers });
        if (cotmRes.data.candidates?.length > 0) {
          const sorted = cotmRes.data.candidates.sort((a, b) => b.vote_count - a.vote_count);
          setCarOfMonth(sorted[0]);
        }
      } catch (e) {}

      // Fetch active surveys
      try {
        const surveyRes = await axios.get(`${API_URL}/api/public/surveys/active`);
        setActiveSurveys(surveyRes.data.surveys || []);
      } catch (e) {}

      // Fetch recent marketplace listings
      try {
        const mktRes = await axios.get(`${API_URL}/api/marketplace/listings?type=sell`, { headers });
        setRecentListings((mktRes.data.listings || []).filter(l => l.status === 'active').slice(0, 4));
      } catch (e) {}

      // Fetch unread messages
      try {
        const msgRes = await axios.get(`${API_URL}/api/marketplace/messages/unread-count`, { headers });
        setUnreadMessages(msgRes.data.unread_count || 0);
      } catch (e) {}

    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-CH', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Welcome Header */}
        <div className="bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/30 p-4 sm:p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-['Oswald'] text-2xl sm:text-3xl font-bold text-foreground uppercase tracking-tight">
                  Willkommen, {user?.name?.split(' ')[0] || 'Mitglied'}!
                </h1>
                <Link to="/about" className="text-xs px-2 py-0.5 bg-primary/30 text-primary font-mono hover:bg-primary/40 transition-colors">
                  v{APP_VERSION}
                </Link>
              </div>
              <p className="text-muted-foreground mt-1">
                Swiss Historic Racing Team Mitgliederbereich
              </p>
            </div>
          </div>
        </div>

        {/* Member Directory - Most Important Button at Top */}
        <Link
          to="/members"
          className="flex items-center justify-between p-4 sm:p-5 bg-primary/10 border-2 border-primary hover:bg-primary/20 transition-colors"
          data-testid="member-directory-link"
        >
          <div className="flex items-center gap-3">
            <Users className="text-primary" size={28} />
            <div>
              <span className="text-lg font-bold uppercase tracking-wider">Mitgliederverzeichnis</span>
              <p className="text-sm text-muted-foreground">Alle Mitglieder anzeigen</p>
            </div>
          </div>
          <ChevronRight className="text-primary" size={24} />
        </Link>

        {/* Alerts: Open Invoices */}
        {openInvoices.length > 0 && (
          <Link 
            to="/my-invoices"
            className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 hover:bg-yellow-500/20 transition-colors"
          >
            <AlertCircle className="text-yellow-500 flex-shrink-0" size={24} />
            <div className="flex-1">
              <p className="font-medium">
                {openInvoices.length} offene {openInvoices.length === 1 ? 'Rechnung' : 'Rechnungen'}
              </p>
              <p className="text-sm text-muted-foreground">
                CHF {openInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0).toFixed(2)} ausstehend
              </p>
            </div>
            <ChevronRight className="text-yellow-500" size={20} />
          </Link>
        )}

        {/* Unread Messages Alert */}
        {unreadMessages > 0 && (
          <Link
            to="/messages"
            className="flex items-center gap-3 p-4 bg-primary/10 border border-primary/30 hover:bg-primary/20 transition-colors"
            data-testid="unread-messages-alert"
          >
            <div className="relative flex-shrink-0">
              <MessageCircle className="text-primary" size={24} />
              <span className="absolute -top-1.5 -right-1.5 bg-primary text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">
                {unreadMessages > 99 ? '99+' : unreadMessages}
              </span>
            </div>
            <div className="flex-1">
              <p className="font-medium">
                {unreadMessages} ungelesene {unreadMessages === 1 ? 'Nachricht' : 'Nachrichten'}
              </p>
              <p className="text-sm text-muted-foreground">Marktplatz-Nachrichten anzeigen</p>
            </div>
            <ChevronRight className="text-primary" size={20} />
          </Link>
        )}

        {/* Active Surveys */}
        {activeSurveys.length > 0 && activeSurveys.map(survey => (
          <a
            key={survey.id}
            href={`${window.location.origin}/public/survey/${survey.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 sm:p-5 bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 transition-colors"
            data-testid={`survey-banner-${survey.id}`}
          >
            <ClipboardList className="text-blue-500 flex-shrink-0" size={28} />
            <div className="flex-1 min-w-0">
              <span className="font-bold uppercase tracking-wider">{survey.title}</span>
              {survey.description && (
                <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{survey.description}</p>
              )}
              <p className="text-xs text-muted-foreground mt-0.5">{survey.responses_count || 0} Teilnahmen</p>
            </div>
            <span className="text-xs font-semibold text-blue-500 uppercase tracking-wider flex-shrink-0">Jetzt teilnehmen</span>
            <ChevronRight className="text-blue-500 flex-shrink-0" size={20} />
          </a>
        ))}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link
            to="/events"
            className="p-4 bg-card border border-border hover:border-primary/50 transition-colors text-center"
          >
            <Calendar className="mx-auto mb-2 text-primary" size={28} />
            <p className="text-sm font-medium">Kalender</p>
          </Link>
          <Link
            to="/vehicles"
            className="p-4 bg-card border border-border hover:border-primary/50 transition-colors text-center"
          >
            <Car className="mx-auto mb-2 text-purple-500" size={28} />
            <p className="text-sm font-medium">Meine Fahrzeuge</p>
          </Link>
          <Link
            to="/my-invoices"
            className="p-4 bg-card border border-border hover:border-primary/50 transition-colors text-center"
          >
            <Receipt className="mx-auto mb-2 text-green-500" size={28} />
            <p className="text-sm font-medium">Rechnungen</p>
          </Link>
          <Link
            to="/car-of-the-month"
            className="p-4 bg-card border border-border hover:border-primary/50 transition-colors text-center"
          >
            <Trophy className="mx-auto mb-2 text-yellow-500" size={28} />
            <p className="text-sm font-medium">Auto des Monats</p>
          </Link>
        </div>

        {/* Marketplace Preview */}
        {recentListings.length > 0 && (
          <div className="bg-card border border-border">
            <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-['Oswald'] text-sm sm:text-base font-bold uppercase tracking-wider flex items-center gap-2">
                <ShoppingBag size={18} className="text-primary" />
                Marktplatz
              </h2>
              <Link to="/marketplace" className="text-xs text-primary hover:underline">Alle Inserate</Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3">
              {recentListings.map(listing => (
                <Link key={listing.id} to={`/marketplace/${listing.id}`} className="group" data-testid={`home-listing-${listing.id}`}>
                  <div className="aspect-[4/3] bg-muted/30 overflow-hidden relative">
                    {listing.images?.[0]?.url ? (
                      <img src={listing.images[0].url} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Car className="text-muted-foreground/30" size={32} /></div>
                    )}
                  </div>
                  <p className="text-xs font-medium mt-1 truncate">{listing.title}</p>
                  <p className="text-[10px] text-primary font-semibold">
                    {listing.price_on_request || listing.price == null ? 'Preis auf Anfrage' : `CHF ${listing.price.toLocaleString('de-CH')}`}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          
          {/* My Event Registrations / Tickets */}
          <div className="bg-card border border-border">
            <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-['Oswald'] text-sm sm:text-base font-bold uppercase tracking-wider flex items-center gap-2">
                <Ticket size={18} className="text-green-500" />
                Meine Tickets
              </h2>
              <Link to="/events" className="text-xs text-primary hover:underline">Alle Events</Link>
            </div>
            {myRegistrations.length > 0 ? (
              <div className="divide-y divide-border">
                {myRegistrations.slice(0, 3).map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors"
                  >
                    <Link to={`/events/${event.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-12 h-12 bg-green-500/10 flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-green-500">
                          {new Date(event.date).getDate()}
                        </span>
                        <span className="text-[9px] text-green-500 uppercase">
                          {new Date(event.date).toLocaleDateString('de-CH', { month: 'short' })}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground text-sm truncate">{event.title}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin size={10} />
                          {event.location || 'Ort TBD'}
                        </p>
                      </div>
                    </Link>
                    <Button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const token = localStorage.getItem('shrt_token');
                        try {
                          const response = await axios.get(`${API_URL}/api/events/${event.id}/ticket`, {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob'
                          });
                          
                          // Create blob URL and trigger download
                          const blob = new Blob([response.data], { type: 'application/pdf' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          const safeTitle = (event.title || 'event').replace(/[^a-zA-Z0-9]/g, '_');
                          link.setAttribute('download', `Ticket_${safeTitle}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                          toast.success('Ticket heruntergeladen');
                        } catch (error) {
                          console.error('Ticket download error:', error);
                          toast.error('Fehler beim Herunterladen des Tickets');
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="rounded-none flex-shrink-0"
                      data-testid="download-ticket-btn"
                    >
                      <Download size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-muted-foreground text-sm">
                <Ticket className="mx-auto mb-2 opacity-50" size={24} />
                <p>Keine Tickets</p>
                <Link to="/events" className="text-primary text-xs hover:underline">Events entdecken</Link>
              </div>
            )}
          </div>

          {/* Upcoming Events */}
          <div className="bg-card border border-border">
            <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-['Oswald'] text-sm sm:text-base font-bold uppercase tracking-wider flex items-center gap-2">
                <Calendar size={18} className="text-primary" />
                Nächste Events
              </h2>
            </div>
            {upcomingEvents.length > 0 ? (
              <div className="divide-y divide-border">
                {upcomingEvents.filter(e => !e.my_registration).slice(0, 3).map((event) => (
                  <Link
                    key={event.id}
                    to={`/events/${event.id}`}
                    className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary/10 flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-primary">
                        {new Date(event.date).getDate()}
                      </span>
                      <span className="text-[9px] text-primary uppercase">
                        {new Date(event.date).toLocaleDateString('de-CH', { month: 'short' })}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground text-sm truncate">{event.title}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin size={10} />
                        {event.location || 'Ort TBD'}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-muted-foreground text-sm">
                <p>Keine anstehenden Events</p>
              </div>
            )}
          </div>
        </div>

        {/* My Vehicles */}
        <div className="bg-card border border-border">
          <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-['Oswald'] text-sm sm:text-base font-bold uppercase tracking-wider flex items-center gap-2">
              <Car size={18} className="text-purple-500" />
              Meine Fahrzeuge
            </h2>
            <Link to="/vehicles" className="text-xs text-primary hover:underline">Verwalten</Link>
          </div>
          {myVehicles.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 p-3">
              {myVehicles.slice(0, 4).map((vehicle) => (
                <Link
                  key={vehicle.id}
                  to="/vehicles"
                  className="group"
                >
                  <div className="aspect-[4/3] bg-muted/30 overflow-hidden">
                    {vehicle.images?.[0]?.url ? (
                      <img 
                        src={vehicle.images[0].url} 
                        alt={vehicle.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Car className="text-muted-foreground/30" size={32} />
                      </div>
                    )}
                  </div>
                  <p className="text-xs font-medium mt-1 truncate">{vehicle.name}</p>
                  {vehicle.year && (
                    <p className="text-[10px] text-muted-foreground">{vehicle.year}</p>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-muted-foreground text-sm">
              <Car className="mx-auto mb-2 opacity-50" size={24} />
              <p>Keine Fahrzeuge erfasst</p>
              <Link to="/vehicles" className="text-primary text-xs hover:underline">Fahrzeug hinzufügen</Link>
            </div>
          )}
        </div>

        {/* Car of the Month */}
        {carOfMonth && (
          <Link 
            to="/car-of-the-month"
            className="block bg-gradient-to-r from-yellow-500/10 to-yellow-500/5 border border-yellow-500/30 hover:border-yellow-500/50 transition-colors"
          >
            <div className="p-4 flex items-center gap-4">
              <div className="w-16 h-16 bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <Trophy className="text-yellow-500" size={32} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-yellow-500 uppercase tracking-wider font-bold">Auto des Monats</p>
                <p className="font-medium text-foreground truncate">{carOfMonth.vehicle_name}</p>
                <p className="text-sm text-muted-foreground">{carOfMonth.owner_name} · {carOfMonth.vote_count} Stimmen</p>
              </div>
              <ChevronRight className="text-yellow-500 flex-shrink-0" size={20} />
            </div>
          </Link>
        )}

        {/* Quick Links */}
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/profile/edit"
            className="flex items-center gap-3 p-4 bg-card border border-border hover:border-primary/50 transition-colors"
          >
            <User className="text-primary" size={20} />
            <span className="text-sm font-medium">Mein Profil</span>
          </Link>
          <Link
            to="/member-vehicles"
            className="flex items-center gap-3 p-4 bg-card border border-border hover:border-primary/50 transition-colors"
          >
            <Heart className="text-primary" size={20} />
            <span className="text-sm font-medium">Fahrzeug-Galerie</span>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
